import { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Save, Code, BarChart3, Search, Globe } from "lucide-react";

export default function AdminScripts() {
  const { t } = useI18n();
  const [scripts, setScripts] = useState({
    google_analytics_id: "", google_webmaster_verification: "",
    head_scripts: "", body_scripts: "", footer_scripts: "",
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/scripts").then((r) => {
      const data = {};
      r.data.forEach((s) => { data[s.key] = s.value; });
      setScripts((prev) => ({ ...prev, ...data }));
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    try {
      await api.put("/admin/scripts", scripts);
      toast.success(t("admin.scripts_saved"));
    } catch { toast.error(t("common.error")); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">{t("common.loading")}</div>;

  return (
    <div className="space-y-6" data-testid="admin-scripts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t("admin.scripts_title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("admin.scripts_subtitle")}</p>
        </div>
        <Button onClick={save} className="bg-[#2CA8AC] hover:bg-[#248F92]" data-testid="save-scripts-btn">
          <Save className="w-4 h-4 mr-2" />{t("admin.save")}
        </Button>
      </div>

      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2"><BarChart3 className="w-4 h-4 text-amber-500" />{t("admin.ga_title")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>{t("admin.ga_label")}</Label>
            <Input value={scripts.google_analytics_id} onChange={(e) => setScripts({...scripts, google_analytics_id: e.target.value})} placeholder="G-XXXXXXXXXX" className="mt-1 font-mono-data" data-testid="ga-id-input" />
            <p className="text-xs text-muted-foreground mt-1">{t("admin.ga_hint")}</p>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2"><Search className="w-4 h-4 text-blue-500" />{t("admin.gsc_title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <Label>{t("admin.gsc_label")}</Label>
          <Input value={scripts.google_webmaster_verification} onChange={(e) => setScripts({...scripts, google_webmaster_verification: e.target.value})} className="mt-1 font-mono-data" data-testid="gwt-input" />
        </CardContent>
      </Card>

      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2"><Code className="w-4 h-4 text-emerald-500" />{t("admin.head_scripts")}</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea rows={6} value={scripts.head_scripts} onChange={(e) => setScripts({...scripts, head_scripts: e.target.value})} className="font-mono-data text-sm" data-testid="head-scripts-input" />
          <p className="text-xs text-muted-foreground mt-1">{t("admin.head_scripts_hint")}</p>
        </CardContent>
      </Card>

      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2"><Code className="w-4 h-4 text-purple-500" />{t("admin.footer_scripts")}</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea rows={6} value={scripts.footer_scripts} onChange={(e) => setScripts({...scripts, footer_scripts: e.target.value})} className="font-mono-data text-sm" data-testid="footer-scripts-input" />
          <p className="text-xs text-muted-foreground mt-1">{t("admin.footer_scripts_hint")}</p>
        </CardContent>
      </Card>
    </div>
  );
}
